import * as Sentry from "@sentry/vue"
import { addBreadcrumb, captureException as sentryCaptureException, setSentryUser } from './sentry'
import router from '@/router'

// Error types that should be ignored
const IGNORED_ERRORS = [
  'Image failed to load!And failed src was',
  'ResizeObserver loop limit exceeded',
  'ResizeObserver loop completed with undelivered notifications',
  'Non-Error promise rejection captured',
  'canceled',
  'Cannot read properties of null',
  "Cannot read properties of null (reading '$')",
  '_AutofillCallbackHandler',
  "Can't find variable: _AutofillCallbackHandler",
]

// HTTP status codes that shouldn't be reported
const IGNORED_HTTP_STATUSES = [401, 406]

/**
 * Centralized error handler - use this for all error reporting
 * @param {Error|string} error - The error object or message
 * @param {Object} context - Additional context about the error
 * @param {string} context.category - Error category (e.g., 'api', 'component', 'user-action')
 * @param {string} context.action - What action was being performed
 * @param {Object} context.data - Additional data about the error
 * @param {string} context.component - Component name where error occurred
 * @param {boolean} context.fatal - Whether this is a fatal error
 */
export const handleError = (error, context = {}) => {
  const {
    category = 'general',
    action = 'unknown',
    data = {},
    component = 'unknown',
    fatal = false,
    skipSentry = false,
  } = context

  // Extract error message
  const errorMessage = error?.message || error?.toString() || String(error)
  const errorStack = error?.stack || ''

  // Check if error should be ignored
  if (shouldIgnoreError(errorMessage, errorStack)) {
    return
  }

  // Log to console in development
  if (import.meta.env.DEV) {
    console.error('🚨 Error Handler:', {
      error: errorMessage,
      category,
      action,
      component,
      data,
      originalError: error,
    })
  }

  // Add breadcrumb for debugging
  addBreadcrumb(`Error: ${errorMessage}`, category, 'error', {
    action,
    component,
    ...data,
  })

  // Report to Sentry if not skipped
  if (!skipSentry) {
    sentryCaptureException(error, {
      category,
      action,
      component,
      fatal,
      ...data,
      route: router.currentRoute.value?.path,
      url: window.location.href,
    })
  }
}

/**
 * Handle API errors specifically
 * @param {Object} error - Axios error object
 * @param {Object} context - Additional context
 */
export const handleApiError = (error, context = {}) => {
  const status = error?.response?.status
  const statusText = error?.response?.statusText
  const responseData = error?.response?.data
  const requestUrl = error?.config?.url
  const requestMethod = error?.config?.method?.toUpperCase()

  // Check if this is a canceled/aborted request - ignore these
  const isCanceledRequest = 
    error.code === 'ERR_CANCELED' ||
    error.name === 'CanceledError' ||
    error.name === 'AbortError' ||
    error.message === 'canceled' ||
    error.message?.toLowerCase()?.includes('canceled') ||
    String(error).toLowerCase().includes('canceled') ||
    context.data?.error === 'canceled'

  if (isCanceledRequest) {
    return
  }

  // Skip ignored statuses
  if (IGNORED_HTTP_STATUSES.includes(status)) {
    return
  }

  const errorMessage = responseData?.message || error?.message || 'API Error'

  handleError(error, {
    category: 'api',
    action: `${requestMethod} ${requestUrl}`,
    component: context.component || 'API',
    data: {
      status,
      statusText,
      url: requestUrl,
      method: requestMethod,
      response: responseData,
      request: {
        params: error?.config?.params,
        data: error?.config?.data,
      },
    },
    fatal: status >= 500, // Server errors are more critical
  })
}

/**
 * Handle Vue component errors
 * @param {Error} error - The error object
 * @param {Object} instance - Vue component instance
 * @param {string} info - Vue-specific error info
 */
export const handleVueError = (error, instance, info) => {
  const componentName = instance?.$options?.name || 
                       instance?.$options?.__name ||
                       instance?.$options?.__file?.split('/').pop() ||
                       'UnknownComponent'

  handleError(error, {
    category: 'vue',
    action: 'component-error',
    component: componentName,
    data: {
      info,
      props: instance?.$props,
      data: instance?.$data,
    },
    fatal: true, // Vue errors are usually fatal
  })
}

/**
 * Handle unhandled promise rejections
 * @param {Error|any} error - The rejection reason
 */
export const handleUnhandledRejection = (error) => {
  // Skip certain known issues
  if (error?.message?.includes('ResizeObserver')) {
    return
  }

  handleError(error, {
    category: 'promise',
    action: 'unhandled-rejection',
    component: 'Promise',
    data: {
      type: error?.constructor?.name,
    },
    fatal: false,
  })
}

/**
 * Handle general JavaScript errors
 * @param {ErrorEvent} event - Browser error event
 */
export const handleGlobalError = (event) => {
  const { message, filename, lineno, colno, error } = event

  // Skip ignored errors
  if (shouldIgnoreError(message)) {
    return
  }

  handleError(error || new Error(message), {
    category: 'javascript',
    action: 'global-error',
    component: filename?.split('/').pop() || 'Global',
    data: {
      message,
      filename,
      lineno,
      colno,
      url: window.location.href,
    },
    fatal: true,
  })
}

/**
 * Check if error should be ignored
 */
const shouldIgnoreError = (message, stack = '') => {
  if (!message) return false

  const messageStr = String(message).toLowerCase()
  const stackStr = String(stack).toLowerCase()

  // Check for ag-grid Vue component mounting errors during destruction
  if (messageStr.includes('cannot read properties of null') && 
      (messageStr.includes("reading '$'") || stackStr.includes('vuecomponentfactory') || stackStr.includes('ag-grid-vue'))) {
    return true
  }

  // Check for browser autofill/password manager errors
  if (messageStr.includes('autofillcallbackhandler') || 
      messageStr.includes("can't find variable: _autofillcallbackhandler") ||
      (messageStr.includes('referenceerror') && messageStr.includes('autofill'))) {
    return true
  }

  return IGNORED_ERRORS.some(ignored => 
    messageStr.includes(ignored.toLowerCase()) ||
    stackStr.includes(ignored.toLowerCase())
  )
}

/**
 * Track user actions as breadcrumbs for better debugging
 * @param {string} action - Action name
 * @param {Object} data - Action data
 */
export const trackAction = (action, data = {}) => {
  addBreadcrumb(action, 'user-action', 'info', {
    ...data,
    route: router.currentRoute.value?.path,
  })
}

/**
 * Track navigation for better debugging
 * @param {Object} to - Route object
 * @param {Object} from - Previous route
 */
export const trackNavigation = (to, from) => {
  addBreadcrumb('Navigation', 'navigation', 'info', {
    from: from?.path,
    to: to?.path,
    params: to?.params,
    query: to?.query,
  })
}

/**
 * Set user context when user logs in
 * @param {Object} user - User object
 */
export const setErrorHandlerUser = (user) => {
  setSentryUser(user)
  
  addBreadcrumb('User logged in', 'auth', 'info', {
    userId: user?.id,
    email: user?.email,
  })
}

/**
 * Clear user context on logout
 */
export const clearErrorHandlerUser = () => {
  setSentryUser(null)
  addBreadcrumb('User logged out', 'auth', 'info')
}

